/**
 * Exclusion mutuelle par nom : un seul renouvellement de session, une seule
 * tournée d'envoi à la fois. Entre onglets par Web Locks quand le navigateur
 * les offre ; sinon (jsdom, contexte non sécurisé `http://IP:5173`, navigateur
 * ancien) dans l'onglet seulement, l'idempotence de l'API couvrant le reste.
 * Aucun bail en IndexedDB : un onglet tué ne laisse jamais de verrou orphelin.
 */

/** Rendu par `essayer` quand le verrou est déjà tenu. */
export const OCCUPE: unique symbol = Symbol('occupe');

export interface Verrou {
  /** Exclusif (entre onglets si navigator.locks existe) ; attend son tour. */
  attendre<T>(nom: string, travail: () => Promise<T>): Promise<T>;
  /** Ne s'exécute que si le verrou est libre. */
  essayer<T>(nom: string, travail: () => Promise<T>): Promise<T | typeof OCCUPE>;
}

export const VERROU_RENOUVELLEMENT = 'rh-app:auth:renouvellement';

/** Exclusion dans l'onglet : une file de promesses par nom. */
export function verrouMemoire(): Verrou {
  // Fin du dernier travail inscrit, et nombre de travaux inscrits (en cours ou
  // en attente) : tant qu'il y en a un, le nom est occupé.
  const fins = new Map<string, Promise<void>>();
  const inscrits = new Map<string, number>();

  function attendre<T>(nom: string, travail: () => Promise<T>): Promise<T> {
    const precedent = fins.get(nom) ?? Promise.resolve();
    inscrits.set(nom, (inscrits.get(nom) ?? 0) + 1);
    const liberer = () => {
      const restants = (inscrits.get(nom) ?? 1) - 1;
      if (restants > 0) {
        inscrits.set(nom, restants);
      } else {
        inscrits.delete(nom);
        fins.delete(nom);
      }
    };
    // Le nom est rendu avant que l'appelant ne reçoive le résultat : un
    // `essayer` enchaîné juste après un `attendre` le trouve libre.
    const resultat = precedent.then(async () => {
      try {
        return await travail();
      } finally {
        liberer();
      }
    });
    fins.set(
      nom,
      resultat.then(
        () => undefined,
        () => undefined,
      ),
    );
    return resultat;
  }

  return {
    attendre,
    async essayer<T>(nom: string, travail: () => Promise<T>): Promise<T | typeof OCCUPE> {
      if ((inscrits.get(nom) ?? 0) > 0) return OCCUPE;
      return attendre(nom, travail);
    },
  };
}

/**
 * Web Locks si le navigateur les expose, repli automatique sur `verrouMemoire`
 * sinon. Une demande refusée avant d'être accordée (document sans origine,
 * politique du navigateur) bascule elle aussi sur le repli, sans rien perdre.
 * `gestionnaire` n'est passé que par les tests.
 */
export function verrouNavigateur(gestionnaire?: LockManager | null): Verrou {
  const verrous =
    gestionnaire === undefined
      ? (globalThis.navigator as (Navigator & { locks?: LockManager }) | undefined)?.locks
      : gestionnaire;
  const repli = verrouMemoire();
  if (!verrous || typeof verrous.request !== 'function') return repli;

  async function demander<T>(
    nom: string,
    options: LockOptions,
    travail: (accorde: boolean) => Promise<T>,
    secours: () => Promise<T>,
  ): Promise<T> {
    let appele = false;
    try {
      return await verrous!.request(nom, options, (verrou) => {
        appele = true;
        return travail(verrou !== null);
      });
    } catch (erreur) {
      if (appele) throw erreur;
      return secours();
    }
  }

  return {
    attendre: (nom, travail) =>
      demander(nom, { mode: 'exclusive' }, () => travail(), () => repli.attendre(nom, travail)),
    essayer: <T>(nom: string, travail: () => Promise<T>) =>
      demander<T | typeof OCCUPE>(
        nom,
        { mode: 'exclusive', ifAvailable: true },
        (accorde) => (accorde ? travail() : Promise.resolve(OCCUPE)),
        () => repli.essayer(nom, travail),
      ),
  };
}
